import React, { useState, useEffect, useRef } from 'react';
import { FaTimesCircle, FaSearch } from 'react-icons/fa';

/**
 * Tag selector — select-from-list only.
 * The vocabulary is locked (R6 / A5): inline tag creation has been removed.
 * New tags enter one way: Tim approves, a developer edits the seeder and re-seeds.
 */
const TagInput = ({
  label,
  selectedTags = [],
  onTagSelect,
  onTagRemove,
  onSearch,
  isLoading = false,
  placeholder = 'Search and add tags...',
  disabled = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleTagSelect = (tag) => {
    setSearchQuery('');
    onTagSelect(tag);
    setShowSuggestions(false);
  };

  const suggestions = onSearch(searchQuery) || [];

  // Whether the typed string has no exact match in the current suggestion list.
  const noMatch = searchQuery.trim() &&
    !suggestions.some(tag => tag.name.toLowerCase() === searchQuery.trim().toLowerCase());

  return (
    <div className="form-control">
      {label && <label>{label}</label>}

      {/* Selected tags */}
      <div className="selected-tags mb-2">
        {selectedTags.map(tag => (
          <span key={tag.id} className="tag-badge">
            {tag.name}
            <button
              type="button"
              className="tag-remove"
              onClick={(e) => {
                e.preventDefault();
                onTagRemove(tag.id);
              }}
              disabled={disabled}
            >
              <FaTimesCircle />
            </button>
          </span>
        ))}
      </div>

      {/* Search input */}
      <div className="position-relative" ref={containerRef}>
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="form-control"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setShowSuggestions(true)}
            disabled={disabled}
          />
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && (
          <div className="tag-suggestions">
            {isLoading ? (
              <div className="p-2 text-muted">Loading...</div>
            ) : suggestions.length > 0 ? (
              <>
                {suggestions.map(tag => (
                  <div
                    key={tag.id}
                    className="suggestion-item"
                    onClick={() => handleTagSelect(tag)}
                  >
                    {tag.name}
                  </div>
                ))}
                {/* No create option — vocabulary is locked. */}
                {noMatch && (
                  <div className="p-2 text-muted" style={{ fontSize: '0.85rem' }}>
                    No matching tag. The tag list is fixed — contact Tim to add one.
                  </div>
                )}
              </>
            ) : searchQuery.trim() ? (
              <div className="p-2 text-muted" style={{ fontSize: '0.85rem' }}>
                No matching tag. The tag list is fixed — contact Tim to add one.
              </div>
            ) : (
              <div className="p-2 text-muted">Start typing to search tags</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagInput;
